import { Box, Typography, useTheme, useMediaQuery, IconButton, Tooltip, Popover } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar from '../exo/ExoAvatar';
import { ChatEntityReference } from './ChatEntityReference';
import { ChatImageGallery } from './ChatImageGallery';
import { useChatClient } from './useChatClient';
import MediaPreview from '../exo/explorer/MediaPreview';
import ReplyIcon from '@mui/icons-material/Reply';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import DownloadIcon from '@mui/icons-material/Download';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import linkifyHtml from 'linkify-html';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👀'];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const ChatMessage = ({ message, isOwn, showAvatar, showTime = true }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { setReplyToMessage, addReaction, removeReaction } = useChatClient();
  const [showActions, setShowActions] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [reactionDetailsAnchor, setReactionDetailsAnchor] = useState(null);
  const [selectedReaction, setSelectedReaction] = useState(null);
  const [pendingReaction, setPendingReaction] = useState(false);

  const handleReply = () => {
    setReplyToMessage(message);
    setShowActions(false);
  };

  const handleOpenEmoji = (event) => {
    setEmojiAnchor(event.currentTarget);
  };

  const handleCloseEmoji = () => {
    setEmojiAnchor(null);
    setShowActions(false);
  };

  const handleAddReaction = async (emoji) => {
    if (pendingReaction) return;
    setPendingReaction(true);
    try {
      await addReaction(message.id, emoji);
    } catch (e) {
      console.error('Failed to add reaction:', e);
    } finally {
      setPendingReaction(false);
    }
    handleCloseEmoji();
  };

  const handleReactionClick = async (event, reaction) => {
    event.stopPropagation();
    if (pendingReaction) return;
    setPendingReaction(true);
    try {
      if (reaction.hasReacted) {
        await removeReaction(message.id, reaction.emoji);
      } else {
        await addReaction(message.id, reaction.emoji);
      }
    } catch (e) {
      console.error('Failed to toggle reaction:', e);
    } finally {
      setPendingReaction(false);
    }
  };

  const handleReactionLongPress = (event, reaction) => {
    event.stopPropagation();
    setSelectedReaction(reaction);
    setReactionDetailsAnchor(event.currentTarget);
  };

  const handleCloseReactionDetails = () => {
    setReactionDetailsAnchor(null);
    setSelectedReaction(null);
  };

  // Toggle actions on tap for mobile
  const handleMessageClick = () => {
    if (isMobile) {
      setShowActions(prev => !prev);
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), 'HH:mm', { locale: de });
    } catch {
      return '';
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  // Separate images from other attachments
  const { imageAttachments, otherAttachments } = useMemo(() => {
    const attachments = message.attachments || [];
    return {
      imageAttachments: attachments.filter(f => f.mimeType?.startsWith('image/')),
      otherAttachments: attachments.filter(f => !f.mimeType?.startsWith('image/')),
    };
  }, [message.attachments]);

  return (
    <Box
      // Lets the list find this row to scroll to it after a jump from search
      data-message-id={message.id}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
      onMouseEnter={() => !isMobile && setShowActions(true)}
      onMouseLeave={() => !isMobile && setShowActions(false)}
    >
      {!isOwn && (
        <Box sx={{ width: 32, mr: 1, flexShrink: 0 }}>
          {showAvatar && (
            <ExoAvatar
              size="32px"
              name={`${message.sender?.firstName || ''} ${message.sender?.lastName || ''}`}
              picture={message.sender?.picture?.temporaryUrl || message.sender?.picture?.url}
            />
          )}
        </Box>
      )}

      <Box className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`} sx={{ maxWidth: isMobile ? '85%' : '70%' }}>
        {!isOwn && showAvatar && (
          <Typography
            variant="caption"
            sx={{ color: colors.text, opacity: 0.5, mb: 0.5, ml: 1, fontSize: '0.7rem' }}
          >
            {message.sender?.firstName} {message.sender?.lastName}
          </Typography>
        )}

        {message.replyTo && (
          <Box
            className="px-3 py-1.5 mb-1 rounded-lg border-l-2"
            sx={{
              backgroundColor: colors.bgInput,
              borderColor: message.replyTo.deletedAt ? colors.text : colors.info,
              maxWidth: '100%',
              opacity: message.replyTo.deletedAt ? 0.5 : 1,
            }}
          >
            {message.replyTo.deletedAt ? (
              <Typography
                variant="body2"
                noWrap
                sx={{ color: colors.text, fontStyle: 'italic', fontSize: '0.8rem' }}
              >
                {t('This message was deleted')}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: colors.info, fontWeight: 500 }}>
                  {message.replyTo.sender?.firstName}
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ color: colors.text, opacity: 0.6, maxWidth: isMobile ? 180 : 200, fontSize: '0.8rem' }}
                >
                  {stripHtml(message.replyTo.content)}
                </Typography>
              </>
            )}
          </Box>
        )}

        <Box className="flex items-end" sx={{ position: 'relative' }}>
          {/* Action buttons with slide animation */}
          <Box
            className={`flex ${isOwn ? 'order-first' : 'order-last'}`}
            sx={{
              position: 'absolute',
              [isOwn ? 'right' : 'left']: '100%',
              [isOwn ? 'mr' : 'ml']: 0.5,
              bottom: 0,
              opacity: showActions ? 1 : 0,
              transform: showActions
                ? 'translateX(0)'
                : isOwn
                  ? 'translateX(8px)'
                  : 'translateX(-8px)',
              transition: 'opacity 0.15s ease, transform 0.15s ease',
              pointerEvents: showActions ? 'auto' : 'none',
              '& .MuiIconButton-root': {
                color: colors.text,
                opacity: 0.6,
                p: isMobile ? 1 : 0.5,
                '&:hover': { opacity: 1, backgroundColor: colors.bgInput },
              },
            }}
          >
            <Tooltip title={t('Reply')} disableHoverListener={isMobile}>
              <IconButton size="small" onClick={handleReply}>
                <ReplyIcon sx={{ fontSize: isMobile ? 20 : 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('React')} disableHoverListener={isMobile}>
              <IconButton size="small" onClick={handleOpenEmoji}>
                <EmojiEmotionsIcon sx={{ fontSize: isMobile ? 20 : 16 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            onClick={handleMessageClick}
            className="px-3 py-2"
            sx={{
              backgroundColor: message.isUrgent
                ? `${colors.error}15`
                : isOwn
                  ? `${colors.info}25`
                  : colors.card,
              border: message.isUrgent
                ? `2px solid ${colors.error}`
                : isOwn
                  ? `1px solid ${colors.info}40`
                  : 'none',
              borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              cursor: isMobile ? 'pointer' : 'default',
              position: 'relative',
            }}
          >
            {message.isUrgent && (
              <Box
                className="flex items-center gap-0.5 mb-1"
                sx={{ color: colors.error }}
              >
                <PriorityHighIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  {t('Urgent')}
                </Typography>
              </Box>
            )}
            <Typography
              variant="body2"
              sx={{
                color: colors.text,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.4,
                '& a': { color: 'inherit', textDecoration: 'underline' },
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  // linkify before sanitize so the injected anchors get
                  // sanitized as well; ADD_ATTR keeps target, which the html
                  // profile strips, so links open in a new tab
                  linkifyHtml(message.content ?? '', {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    defaultProtocol: 'https',
                  }),
                  {
                    USE_PROFILES: { html: true },
                    ADD_ATTR: ['target'],
                    FORBID_TAGS: ['script', 'style'],
                    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover'],
                  },
                ),
              }}
            />

            {/* Image attachments - use gallery */}
            {imageAttachments.length > 0 && (
              <Box className="mt-2" onClick={(e) => e.stopPropagation()}>
                <ChatImageGallery images={imageAttachments} isOwn={isOwn} />
              </Box>
            )}

            {/* Other attachments - file list */}
            {otherAttachments.length > 0 && (
              <Box className="flex flex-wrap gap-2 mt-2">
                {otherAttachments.map((file) => (
                  <Box
                    key={file.id}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    sx={{
                      backgroundColor: isOwn ? `${colors.info}15` : colors.bgInput,
                      minWidth: 180,
                    }}
                  >
                    <MediaPreview
                      size="40px"
                      src={file.temporaryUrl || file.thumbnailUrl}
                      mimeType={file.mimeType || 'application/octet-stream'}
                      allowPreview={true}
                      allowDownload={false}
                    />
                    <Box className="flex-1 min-w-0">
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: colors.text, fontWeight: 500, display: 'block' }}
                      >
                        {file.title || t('File')}
                      </Typography>
                      {file.fileSize && (
                        <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, fontSize: '0.65rem' }}>
                          {formatFileSize(file.fileSize)}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(file.temporaryUrl, '_blank');
                      }}
                      sx={{ color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
                    >
                      <DownloadIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {message.references?.length > 0 && (
              <Box className="flex flex-wrap gap-1 mt-2">
                {message.references.map((ref) => (
                  <ChatEntityReference key={ref.id} reference={ref} isOwn={isOwn} />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {message.reactions?.length > 0 && (
          <Box
            className={`flex flex-wrap gap-0.5 ${isOwn ? 'justify-end' : 'justify-start'}`}
            sx={{ marginTop: '-6px', position: 'relative', zIndex: 1 }}
          >
            {message.reactions.map((reaction) => (
              <Tooltip
                key={reaction.emoji}
                title={
                  <Box className="flex flex-col gap-0.5 py-0.5">
                    {reaction.users?.map((user) => (
                      <Box key={user.id} className="flex items-center gap-1.5">
                        <ExoAvatar size="18px" name={`${user.firstName || ''} ${user.lastName || ''}`} picture={user.picture} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                }
                arrow
                placement="top"
                enterDelay={isMobile ? 99999 : 300}
                enterTouchDelay={300}
                leaveTouchDelay={1500}
              >
                <Box
                  onClick={(e) => handleReactionClick(e, reaction)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    handleReactionLongPress(e, reaction);
                  }}
                  className="flex items-center cursor-pointer transition-all backdrop-blur-md"
                  sx={{
                    opacity: pendingReaction ? 0.6 : 1,
                    pointerEvents: pendingReaction ? 'none' : 'auto',
                    backgroundColor: reaction.hasReacted ? `${colors.info}20` : `${colors.glass}`,
                    borderRadius: '10px',
                    px: reaction.count > 1 ? 0.75 : 0.5,
                    py: 0.25,
                    minHeight: isMobile ? 24 : 20,
                    '&:hover': {
                      backgroundColor: reaction.hasReacted ? `${colors.info}30` : colors.card,
                    },
                  }}
                >
                  <span style={{ fontSize: isMobile ? '0.75rem' : '0.7rem' }}>{reaction.emoji}</span>
                  {reaction.count > 1 && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.6rem', fontWeight: reaction.hasReacted ? 600 : 400, ml: 0.25 }}
                    >
                      {reaction.count}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}

        {/* Reaction Details Popover (for mobile long press) */}
        <Popover
          open={Boolean(reactionDetailsAnchor)}
          anchorEl={reactionDetailsAnchor}
          onClose={handleCloseReactionDetails}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: {
              backgroundColor: colors.glass,
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              p: 1.5,
              minWidth: 150,
            },
          }}
        >
          {selectedReaction && (
            <Box className="flex flex-col gap-1">
              <Box className="flex items-center gap-2 mb-1 pb-1" sx={{ borderBottom: `1px solid ${colors.bgInput}` }}>
                <span style={{ fontSize: '1.2rem' }}>{selectedReaction.emoji}</span>
                <Typography variant="body2" fontWeight={500}>
                  {selectedReaction.count} {selectedReaction.count === 1 ? 'Reaktion' : 'Reaktionen'}
                </Typography>
              </Box>
              {selectedReaction.users?.map((user) => (
                <Box key={user.id} className="flex items-center gap-2 py-0.5">
                  <ExoAvatar size="24px" name={`${user.firstName || ''} ${user.lastName || ''}`} picture={user.picture} />
                  <Typography variant="body2">
                    {user.firstName} {user.lastName}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Popover>

        {(showTime || message.isEdited) && (
          <Box className="flex items-center gap-1 mt-0.5 px-1">
            {showTime && (
              <Typography variant="caption" sx={{ color: colors.text, opacity: 0.4, fontSize: '0.65rem' }}>
                {formatTime(message.createdAt)}
              </Typography>
            )}
            {message.isEdited && (
              <Typography variant="caption" sx={{ color: colors.text, opacity: 0.4, fontSize: '0.65rem' }}>
                ({t('edited')})
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={handleCloseEmoji}
        anchorOrigin={{
          vertical: 'top',
          horizontal: isOwn ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: isOwn ? 'right' : 'left',
        }}
        PaperProps={{
          sx: {
            backgroundColor: colors.glass,
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            p: isMobile ? 1.5 : 1,
          },
        }}
      >
        <Box className="flex gap-1" sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap', maxWidth: isMobile ? 200 : 'none' }}>
          {QUICK_EMOJIS.map((emoji) => (
            <Box
              key={emoji}
              onClick={() => handleAddReaction(emoji)}
              className="flex items-center justify-center rounded-lg cursor-pointer transition-all"
              sx={{
                width: isMobile ? 44 : 32,
                height: isMobile ? 44 : 32,
                '&:hover': {
                  backgroundColor: colors.card,
                  transform: 'scale(1.1)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <span style={{ fontSize: isMobile ? '1.5rem' : '1.2rem' }}>{emoji}</span>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};
